<template>
  <div class="buxida-md-editor" :class="{ 'buxida-md-editor--drop-target': isDragOver }">
    <div class="buxida-md-editor__actions">
      <b-button
        size="sm"
        variant="outline-secondary"
        :disabled="disabled"
        @click="showLibrary = true"
      >
        <feather-icon icon="ImageIcon" /> Bibliothèque d'images
      </b-button>
      <span v-if="dropUploading" class="ml-2 text-muted small">
        <b-spinner small /> Upload en cours…
      </span>
    </div>

    <mavon-editor
      ref="editor"
      :value="value"
      :language="'fr'"
      :subfield="true"
      :default-open="'edit'"
      :toolbars="toolbars"
      :box-shadow="false"
      :transition="false"
      :scroll-style="true"
      :ishljs="true"
      :placeholder="placeholder"
      :style="{ height: height, zIndex: 1 }"
      @change="onChange"
      @drop.native.prevent="onDrop"
      @dragenter.native.prevent="onDragEnter"
      @dragover.native.prevent="onDragOver"
      @dragleave.native.prevent="onDragLeave"
    />

    <image-library-modal
      :visible.sync="showLibrary"
      @insert="onInsertImage"
      @error="onLibraryError"
    />
  </div>
</template>

<script>
// Wrapper autour de mavon-editor pour l'edition des propositions de
// documentation buxida-docs. Centralise la config (toolbar, hauteur)
// et integre la modale "Bibliotheque d'images" (Phase 2).
//
// Choix de stack : `mavon-editor` plutot que `@kangc/v-md-editor` —
// ce dernier a un probleme d'interop ESM/CJS connu avec webpack 4
// (vue-cli 4) qui se manifeste par "Object(...) is not a function"
// au chargement du module base-editor.js.
//
// Insertion d'image : on attaque directement la textarea interne de
// mavon-editor (selectionStart/End) pour inserer au curseur et
// pousser la nouvelle valeur via v-model. mavon-editor ne propose
// pas de slot toolbar pour des actions custom, donc le bouton vit
// dans une barre d'actions au-dessus de l'editeur.

import {
  BButton,
} from 'bootstrap-vue'
import { mavonEditor } from 'mavon-editor'
import 'mavon-editor/dist/css/index.css'
import axios from '@axios'
import ImageLibraryModal from '@/views/dashboard/docs/ImageLibraryModal.vue'

const DOCS_API_BASE = 'https://api2.buxida.com'

export default {
  name: 'MarkdownEditor',
  components: {
    'mavon-editor': mavonEditor,
    BButton,
    ImageLibraryModal,
  },
  props: {
    value: { type: String, default: '' },
    height: { type: String, default: '60vh' },
    placeholder: { type: String, default: 'Ecrire la documentation en Markdown...' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      showLibrary: false,
      // Cache des URLs blob pour les images du preview. Les images vivent
      // derriere la route auth /admin/docs/images/static/:name, donc on
      // ne peut pas laisser le navigateur charger directement le src
      // "_images/<name>" relatif. On telecharge via axios + objectURL, et
      // on reecrit les src dans le DOM du preview a chaque re-render.
      imageBlobs: {}, // { name -> objectURL }
      imageBlobsLoading: {}, // { name -> Promise<string|null> }
      previewObserver: null,
      isDragOver: false,
      dragDepth: 0,
      dropUploading: 0,
      // Config toolbar mavon-editor : on garde les actions de mise en
      // forme et on cache l'imagelink natif (qui ne sait que coller une
      // URL externe — notre bouton "Bibliotheque" couvre le besoin).
      toolbars: {
        bold: true,
        italic: true,
        header: true,
        underline: true,
        strikethrough: true,
        mark: true,
        superscript: true,
        subscript: true,
        quote: true,
        ol: true,
        ul: true,
        link: true,
        imagelink: false,
        code: true,
        table: true,
        fullscreen: true,
        readmodel: true,
        htmlcode: false,
        help: false,
        undo: true,
        redo: true,
        trash: true,
        save: false,
        navigation: true,
        alignleft: true,
        aligncenter: true,
        alignright: true,
        subfield: true,
        preview: true,
      },
    }
  },
  mounted() {
    this.attachPreviewObserver()
  },
  beforeDestroy() {
    if (this.previewObserver) {
      this.previewObserver.disconnect()
      this.previewObserver = null
    }
    this.revokeImageBlobs()
  },
  methods: {
    onChange(value) {
      // mavon-editor emet (value, render) ; on ne remonte que le markdown.
      this.$emit('input', value)
    },
    onLibraryError(message) {
      // On propage vers le parent qui sait afficher un toast.
      this.$emit('library-error', message)
    },
    onInsertImage(item) {
      // item = { name, references, ... } — on assemble la syntaxe MD.
      const alt = this.altFromName(item.name)
      const snippet = `![${alt}](_images/${item.name})`
      this.insertAtCursor(snippet)
    },
    altFromName(name) {
      // Heuristique : "controle-d-acces-1.png" -> "controle-d-acces-1".
      // Suffisant pour l'a11y de base ; l'admin peut editer apres coup.
      return String(name).replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ')
    },
    // ---------- Preview image rewriting ----------
    attachPreviewObserver(retries) {
      // mavon-editor monte le preview au prochain tick ; on retry quelques
      // fois si l'element n'est pas encore dispo (cas defensif).
      const preview = this.$el && this.$el.querySelector('.v-show-content')
      if (!preview) {
        const left = typeof retries === 'number' ? retries : 5
        if (left <= 0) return
        this.$nextTick(() => setTimeout(() => this.attachPreviewObserver(left - 1), 100))
        return
      }
      // Observe les changements de DOM dans le preview. Mavon-editor
      // remplace innerHTML a chaque keystroke (re-render via markdown-it),
      // donc on intercepte le childList. Pas d'observation d'attributs
      // pour eviter une boucle (notre setAttribute('src', ...) en
      // declencherait).
      const obs = new MutationObserver(() => this.rewriteImageSrcs(preview))
      obs.observe(preview, { childList: true, subtree: true })
      this.previewObserver = obs
      // Premiere passe inline pour rendre les images deja presentes.
      this.rewriteImageSrcs(preview)
    },
    rewriteImageSrcs(preview) {
      const imgs = preview.querySelectorAll('img')
      imgs.forEach(img => {
        const src = img.getAttribute('src') || ''
        const m = src.match(/^_images\/([a-zA-Z0-9._-]+)$/)
        if (!m) return
        const name = m[1]
        const cached = this.imageBlobs[name]
        if (cached) {
          if (img.getAttribute('src') !== cached) {
            img.setAttribute('src', cached)
          }
          return
        }
        this.fetchImageBlob(name).then(url => {
          if (url) img.setAttribute('src', url)
        })
      })
    },
    fetchImageBlob(name) {
      if (this.imageBlobs[name]) return Promise.resolve(this.imageBlobs[name])
      if (this.imageBlobsLoading[name]) return this.imageBlobsLoading[name]
      const promise = axios
        .get(`${DOCS_API_BASE}/admin/docs/images/static/${encodeURIComponent(name)}`, {
          responseType: 'blob',
        })
        .then(resp => {
          const url = URL.createObjectURL(resp.data)
          this.$set(this.imageBlobs, name, url)
          delete this.imageBlobsLoading[name]
          return url
        })
        .catch(() => {
          delete this.imageBlobsLoading[name]
          return null
        })
      this.imageBlobsLoading[name] = promise
      return promise
    },
    revokeImageBlobs() {
      Object.values(this.imageBlobs).forEach(url => {
        try { URL.revokeObjectURL(url) } catch (_) { /* ignore */ }
      })
      this.imageBlobs = {}
      this.imageBlobsLoading = {}
    },

    // ---------- Drag-and-drop upload ----------
    onDragEnter() {
      this.dragDepth += 1
      this.isDragOver = true
    },
    onDragOver(ev) {
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy'
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) this.isDragOver = false
    },
    onDrop(ev) {
      this.dragDepth = 0
      this.isDragOver = false
      const files = Array.from((ev.dataTransfer && ev.dataTransfer.files) || [])
        .filter(f => f && f.type && f.type.startsWith('image/'))
      if (!files.length) return
      // Upload sequentiel pour preserver l'ordre d'insertion + curseur.
      // reduce + chaining de promesses au lieu de for+await (eslint).
      return files.reduce(
        (p, file) => p.then(() => this.uploadAndInsert(file)),
        Promise.resolve(),
      )
    },
    async uploadAndInsert(file) {
      this.dropUploading += 1
      try {
        const form = new FormData()
        form.append('file', file, file.name)
        const response = await axios.post(`${DOCS_API_BASE}/admin/docs/images`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const data = response.data || {}
        if (data.res !== 1 || !data.item) {
          throw new Error((data && data.detail) || 'Upload échoué')
        }
        // Insere la syntaxe markdown au curseur courant.
        this.onInsertImage(data.item)
      } catch (err) {
        const msg = (err && err.response && err.response.data && err.response.data.detail)
          || (err && err.message)
          || 'Erreur inconnue'
        this.$emit('library-error', `Upload ${file.name} : ${msg}`)
      } finally {
        this.dropUploading -= 1
      }
    },

    insertAtCursor(text) {
      // mavon-editor expose une textarea normale dans .v-note-edit.
      // On y injecte le snippet a la position du curseur et on emet la
      // nouvelle valeur. Fallback : append a la fin si on ne trouve pas
      // la textarea (cas defensif).
      const textarea = this.$el.querySelector('.v-note-edit textarea')
      if (!textarea) {
        const append = `${this.value.replace(/\s+$/, '')}\n\n${text}\n`
        this.$emit('input', append)
        return
      }
      const start = textarea.selectionStart || 0
      const end = textarea.selectionEnd || 0
      const before = this.value.slice(0, start)
      const after = this.value.slice(end)
      const next = before + text + after
      this.$emit('input', next)
      // Replacer le curseur apres le snippet une fois la valeur propagee.
      this.$nextTick(() => {
        try {
          textarea.focus()
          const pos = start + text.length
          textarea.setSelectionRange(pos, pos)
        } catch (_) { /* ignore */ }
      })
    },
  },
}
</script>

<style scoped>
.buxida-md-editor__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.buxida-md-editor--drop-target ::v-deep .v-note-wrapper {
  outline: 2px dashed #7367f0;
  outline-offset: -4px;
  background: rgba(115, 103, 240, 0.04);
}
.buxida-md-editor ::v-deep .v-note-wrapper {
  border: 1px solid #e3e3e3;
  border-radius: 4px;
  min-height: 400px;
}
.buxida-md-editor ::v-deep .v-note-edit.divarea-wrapper,
.buxida-md-editor ::v-deep .v-note-edit textarea {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace !important;
  font-size: 13px !important;
}
</style>
